import { warnAndNotify } from '@execonline-inc/error-reporting.private';
import { missingLinkError } from '@execonline-inc/links.private';
import { toTask } from '@execonline-inc/maybe-adapter';
import { always, assertNever } from '@kofno/piper';
import { Task } from 'taskarian';
import CreateShareUrlStore from '.';
import { callApi } from '../../../../../../Appy';
import { findLinkBy, findLinkByT } from '../../../../../../Links';
import ReactionComponent, { RCProps } from '../../../../../../ReactionComponent';
import { Link } from '../../../../../../Resource/Types';
import { learningCollectionResourceDecoder } from '../../../../Decoders';
import { LearningCollectionResource } from '../../../../Types';
import LearningCollectionEditStore from '../../../Edit/Store';
import { ShareUrlError, State } from './Types';

interface Props extends RCProps<CreateShareUrlStore> {
  store: CreateShareUrlStore;
  learningCollectionStore: LearningCollectionEditStore;
  learningCollectionResource: LearningCollectionResource;
}

interface CreateShareUrlOut {
  id: number;
}

const shareUrlDeactivateEncoder = (id: number): CreateShareUrlOut => ({
  id,
});

const handleReportsError = (store: CreateShareUrlStore) => (error: ShareUrlError) => {
  store.error(error);
  warnAndNotify('CreateShareUrlReactions', error.kind, error);
};

const createShareUrlReady =
  (learningCollectionStore: LearningCollectionEditStore, store: CreateShareUrlStore) =>
  (learningCollectionResource: LearningCollectionResource) => {
    learningCollectionStore.readyDeepLinks(learningCollectionResource);
    store.ready(learningCollectionResource);
  };

const handleGenerationError = (store: CreateShareUrlStore) => (error: ShareUrlError) => {
  switch (error.kind) {
    case 'missing-link-error':
      break;
    default:
      store.error(error);
      warnAndNotify('CreateShareUrlReactions', error.kind, error);
  }
};

type Generation = Regular | DeepLinks;

interface Regular {
  kind: 'no-generation';
  resource: LearningCollectionResource;
}

interface DeepLinks {
  kind: 'generation';
  resource: LearningCollectionResource;
}

const generationSuccessHandler =
  (learningCollectionStore: LearningCollectionEditStore, store: CreateShareUrlStore) =>
  (generation: Generation) => {
    switch (generation.kind) {
      case 'generation':
        learningCollectionStore.readyDeepLinks(generation.resource);
        break;
      case 'no-generation':
        break;
      default:
        assertNever(generation);
    }

    store.ready(generation.resource);
  };

const fetchGenerationResource = (id: number) => (link: Link) =>
  callApi(
    learningCollectionResourceDecoder,
    shareUrlDeactivateEncoder(id),
  )(link).map<Generation>((resource) => ({
    resource: resource,
    kind: 'generation',
  }));

const useCurrentResource = (learningCollectionResource: LearningCollectionResource) =>
  always(
    Task.succeed<ShareUrlError, Regular>({
      resource: learningCollectionResource,
      kind: 'no-generation',
    }),
  );

const generateShareUrlsWhenNonePresent = (
  learningCollectionResource: LearningCollectionResource,
  store: CreateShareUrlStore,
  learningCollectionStore: LearningCollectionEditStore,
  state: State,
) =>
  Task.succeed<ShareUrlError, ReadonlyArray<Link>>(learningCollectionResource.links)
    .andThen(findLinkByT({ rel: 'generate' }))
    .andThen(fetchGenerationResource(learningCollectionResource.payload.id))
    .orElse(useCurrentResource(state.learningCollectionResource))
    .fork(handleGenerationError(store), generationSuccessHandler(learningCollectionStore, store));

class CreateShareUrlReactions extends ReactionComponent<CreateShareUrlStore, State, Props> {
  tester = () => this.props.store.state;
  effect = (state: State) => {
    const { store, learningCollectionStore, learningCollectionResource } = this.props;
    switch (state.kind) {
      case 'waiting':
        generateShareUrlsWhenNonePresent(
          learningCollectionResource,
          store,
          learningCollectionStore,
          state,
        );
        break;
      case 'ready':
        break;
      case 'creating':
        toTask<ShareUrlError, Link>(
          missingLinkError({ rel: 'create' }),
          findLinkBy({ rel: 'create' }, learningCollectionResource.links),
        )
          .andThen(
            callApi(
              learningCollectionResourceDecoder,
              shareUrlDeactivateEncoder(learningCollectionResource.payload.id),
            ),
          )
          .fork(handleReportsError(store), createShareUrlReady(learningCollectionStore, store));
        break;
      case 'error':
        break;
      default:
        assertNever(state);
    }
  };
}

export default CreateShareUrlReactions;
